// Пин прикреплённой карточки — «с чем работает юзер» (см. api/attachedStyleCard).
//
// Варианты:
//   side  — витринная карточка сбоку виджета /bots (десктоп ≥1100);
//   panel — карточка в зоне инструкции правой колонки /generate (+2 шага);
//   row   — компактная строка внутри тела виджета (мобила/узкие экраны).
//
// Пин информационный: интерактив — крестик «открепить» и медиа-контролы
// видео-обложки (пауза/звук). Тексты RU-хардкод (i18n пина — вместе с
// i18n Идея-мастера, отдельной задачей).
import { useEffect, useRef, useState } from 'react'
import { Coin } from '../icons/Coin'
import { Icon } from '../../public/icons/Icon.tsx'
import type { AttachedStyleCard as AttachedStyleCardData } from '../api/attachedStyleCard'
import { useAutoSound } from '../hooks/useAutoSound'
import { announceTileSound, onTileSoundClaimed, tileSoundToken } from '../utils/tileSound'

interface Props {
	card: AttachedStyleCardData
	variant: 'side' | 'panel' | 'row'
	onDetach: () => void
	className?: string
	/** Демо-трек карточки реально уйдёт референсом в генерацию (Mureka-модель
	 *  выбрана) — пин честно меняет подпись «Выбранный стиль» → «Референс
	 *  звучания». Вычисляется вызывающим через isAudioReferenceActive. */
	audioRefActive?: boolean
}

const CROSS = (
	<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
		<path d="M18 6 6 18M6 6l12 12" />
	</svg>
)

const PLAY = (
	<svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
		<path d="M8 5.5v13l11-6.5z" />
	</svg>
)

const PAUSE = (
	<svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
		<path d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z" />
	</svg>
)

// Видео-обложка пина: автозапуск БЕЗ звука при появлении (браузерная
// политика разрешает автоплей только muted), один проигрыш без цикла;
// контролы — пауза/плей и вкл/выкл звука. Если автоплей всё же заблокирован,
// ролик стоит на первом кадре, кнопка «плей» работает.
// Настройка аккаунта «автоматически включать звук» (users.auto_sound)
// переопределяет стартовый mute — см. utils/autoSound.ts.
function VideoCover({ src }: { src: string }) {
	const ref = useRef<HTMLVideoElement>(null)
	const [playing, setPlaying] = useState(true)
	const autoSound = useAutoSound()
	const [muted, setMuted] = useState(() => !autoSound)
	// Один звучащий источник на страницу (utils/tileSound): включили звук у пина —
	// плитки и плееры глохнут; включили там — глохнет пин.
	const [soundToken] = useState(tileSoundToken)
	useEffect(() => onTileSoundClaimed(soundToken, () => setMuted(true)), [soundToken])
	const toggleMute = () => {
		const next = !muted
		setMuted(next)
		if (!next) announceTileSound(soundToken)
	}

	// muted управляем property через ref: React не пишет атрибут muted в DOM,
	// из-за чего автоплей в части браузеров срывался бы.
	useEffect(() => {
		if (ref.current) ref.current.muted = muted
	}, [muted])

	const togglePlay = () => {
		const v = ref.current
		if (!v) return
		if (v.paused) void v.play()
		else v.pause()
	}

	return (
		<>
			<video
				ref={ref}
				src={src}
				autoPlay
				muted
				playsInline
				preload="metadata"
				onPlay={(e) => {
					setPlaying(true)
					// Автостарт со звуком (настройка аккаунта) — объявляем, остальные глохнут.
					if (!e.currentTarget.muted) announceTileSound(soundToken)
				}}
				onPause={() => setPlaying(false)}
				onEnded={() => setPlaying(false)}
			/>
			<span className="attached-style-card__vctrl">
				<button
					type="button"
					className="attached-style-card__vbtn"
					onClick={togglePlay}
					aria-label={playing ? 'Пауза' : 'Смотреть'}
					title={playing ? 'Пауза' : 'Смотреть'}
				>
					{playing ? PAUSE : PLAY}
				</button>
				<button
					type="button"
					className="attached-style-card__vbtn"
					onClick={toggleMute}
					aria-label={muted ? 'Включить звук' : 'Выключить звук'}
					title={muted ? 'Включить звук' : 'Выключить звук'}
				>
					<Icon id={muted ? 'gen-audio-off' : 'gen-audio'} size={11} />
				</button>
			</span>
		</>
	)
}

// Аудио-обложка пина: обложка карточки + play/pause. БЕЗ автозапуска — звук
// и есть контент (браузеры блокируют незамьюченный автоплей, и это к лучшему):
// юзер сам решает послушать демо-трек. Один контрол, без регулятора звука.
function AudioCover({ src, cover }: { src: string; cover?: string }) {
	const ref = useRef<HTMLAudioElement>(null)
	const [playing, setPlaying] = useState(false)

	const toggle = () => {
		const a = ref.current
		if (!a) return
		if (a.paused) void a.play()
		else a.pause()
	}

	return (
		<>
			{cover ? (
				<img src={cover} alt="" loading="lazy" decoding="async" />
			) : (
				<span className="attached-style-card__cover-empty" aria-hidden="true" />
			)}
			<audio
				ref={ref}
				src={src}
				preload="none"
				onPlay={() => setPlaying(true)}
				onPause={() => setPlaying(false)}
				onEnded={() => setPlaying(false)}
			/>
			<span className="attached-style-card__vctrl">
				<button
					type="button"
					className={`attached-style-card__vbtn${playing ? ' is-playing' : ''}`}
					onClick={toggle}
					aria-label={playing ? 'Пауза' : 'Послушать демо'}
					title={playing ? 'Пауза' : 'Послушать демо'}
				>
					{playing ? PAUSE : PLAY}
				</button>
			</span>
		</>
	)
}

// Аудио-мини-превью row-пина: весь thumb — кнопка play/pause (на узких
// экранах отдельный контрол некуда ставить, а послушать референс важно).
function RowAudioThumb({ src, cover }: { src: string; cover?: string }) {
	const ref = useRef<HTMLAudioElement>(null)
	const [playing, setPlaying] = useState(false)

	const toggle = () => {
		const a = ref.current
		if (!a) return
		if (a.paused) void a.play()
		else a.pause()
	}

	return (
		<button
			type="button"
			className={`attached-style-card__row-thumb attached-style-card__row-thumb--audio${playing ? ' is-playing' : ''}`}
			onClick={toggle}
			aria-label={playing ? 'Пауза' : 'Послушать демо'}
			title={playing ? 'Пауза' : 'Послушать демо'}
		>
			{cover && <img src={cover} alt="" loading="lazy" decoding="async" />}
			<audio
				ref={ref}
				src={src}
				preload="none"
				onPlay={() => setPlaying(true)}
				onPause={() => setPlaying(false)}
				onEnded={() => setPlaying(false)}
			/>
			<span className="attached-style-card__row-thumb-glyph" aria-hidden="true">
				{playing ? PAUSE : PLAY}
			</span>
		</button>
	)
}

export default function AttachedStyleCard({ card, variant, onDetach, className, audioRefActive }: Props) {
	// Подпись пина: аудио-референс активен → говорим правду о роли карточки.
	const pinLabel = audioRefActive ? 'Референс звучания' : 'Выбранный стиль'
	const detachBtn = (
		<button
			type="button"
			className="attached-style-card__detach"
			onClick={onDetach}
			aria-label="Открепить стиль"
			title="Открепить стиль"
		>
			{CROSS}
		</button>
	)

	if (variant === 'row') {
		return (
			<div className={`attached-style-card attached-style-card--row${className ? ` ${className}` : ''}`}>
				{card.audioUrl ? (
					<RowAudioThumb src={card.audioUrl} cover={card.previewUrl} />
				) : card.previewUrl ? (
					<img className="attached-style-card__row-thumb" src={card.previewUrl} alt="" loading="lazy" decoding="async" />
				) : card.videoUrl ? (
					<video className="attached-style-card__row-thumb" src={card.videoUrl} muted playsInline preload="metadata" />
				) : (
					<span className="attached-style-card__row-thumb attached-style-card__row-thumb--empty" aria-hidden="true" />
				)}
				<span className="attached-style-card__row-text">
					<span className="attached-style-card__row-title">{card.title}</span>
					<span className="attached-style-card__row-sub">
						{audioRefActive ? 'референс звучания' : 'выбранный стиль'}
						{card.credits ? ` · ${card.credits} ток.` : ''}
					</span>
				</span>
				{detachBtn}
			</div>
		)
	}

	// side/panel — МОНОЛИТ: шапка, обложка, описание и шаги живут внутри
	// одного тела (отдельная плашка над карточкой читалась разбито).
	// side — вертикальная витринная компоновка (узкая колонка у виджета);
	// panel — горизонтальная (широкая колонка /generate: квадратная обложка
	// слева + текст справа, иначе обложка растягивалась панорамным блином).
	const cover = (
		<div className="attached-style-card__cover">
			{card.audioUrl ? (
				<AudioCover src={card.audioUrl} cover={card.previewUrl} />
			) : card.previewUrl ? (
				<img src={card.previewUrl} alt="" loading="lazy" decoding="async" />
			) : card.videoUrl ? (
				<VideoCover src={card.videoUrl} />
			) : (
				<span className="attached-style-card__cover-empty" aria-hidden="true" />
			)}
			{variant === 'side' && card.credits && (
				<span className="attached-style-card__price">
					{card.credits} <Coin width={13} height={13} />
				</span>
			)}
		</div>
	)

	const info = (
		<div className="attached-style-card__info">
			<p className="attached-style-card__title">{card.title}</p>
			{card.description && <p className="attached-style-card__desc">{card.description}</p>}
			{variant === 'panel' && card.credits && (
				<span className="attached-style-card__price-inline">
					{card.credits} <Coin width={13} height={13} />
				</span>
			)}
		</div>
	)

	return (
		<div className={`attached-style-card attached-style-card--${variant}${className ? ` ${className}` : ''}`}>
			<div className="attached-style-card__body">
				<div className="attached-style-card__head">
					<span className="attached-style-card__label">{pinLabel}</span>
					{detachBtn}
				</div>
				{variant === 'panel' ? (
					<div className="attached-style-card__main">
						{cover}
						{info}
					</div>
				) : (
					<>
						{cover}
						{info}
					</>
				)}
				{variant === 'panel' && (
					<div className="attached-style-card__steps">
						{audioRefActive ? (
							<>
								<p>
									<span>1</span>Опишите свою песню и стиль
								</p>
								<p>
									<span>2</span>Нажмите «Создать» — звучание возьмём из референса
								</p>
							</>
						) : (
							<>
								<p>
									<span>1</span>Добавьте своё фото, если нужно
								</p>
								<p>
									<span>2</span>Нажмите «Создать»
								</p>
							</>
						)}
					</div>
				)}
			</div>
		</div>
	)
}
